<template>
  <div class="mcontainer">
    <!-- Header -->
    <div class="header-bar">
      <h2>BO Approval</h2>
      <div class="actions">
        <div class="search">
          <Icon icon="mdi:magnify" width="18" height="18" class="search-icon" />
          <input
            class="search-input"
            v-model="search"
            @input="debouncedFetch"
            placeholder="Search..."
          />
        </div>
        <select class="form-control" style="margin-bottom: 0;" v-model="status" @change="applyStatus">
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
    </div>

    <!-- Card -->
    <div class="card">
      <table class="table">
        <thead>
          <tr>
            <th style="width:120px">Gambar</th>
            <th>Nama</th>
            <th>Pengusul</th>
            <th>Status</th>
            <th style="width:220px">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in list" :key="it.id">
            <td>
              <img v-if="it.image" :src="it.image" class="thumb" alt="">
              <div v-else class="thumb placeholder">
                <Icon icon="mdi:image-off-outline" width="22" height="22" />
              </div>
            </td>
            <td>
              <div class="name">{{ it.name }}</div>
              <div class="desc muted">{{ truncate(it.description, 120) }}</div>
            </td>
            <td>
              {{ (it.user && it.user.fullname) || '-' }}
              <span class="muted">({{ (it.user && it.user.type) || '-' }})</span>
            </td>
            <td>
              <span :class="['badge', it.status]">{{ it.status }}</span>
            </td>
            <td>
              <div class="row-actions">
                <button
                  class="btn btn-success"
                  @click="approve(it.id)"
                  :disabled="loading || it.status === 'approved'"
                >
                  Approve
                </button>
                <button
                  class="btn btn-warning"
                  @click="reject(it.id)"
                  :disabled="loading || it.status === 'rejected'"
                >
                  Reject
                </button>
                <button
                  class="btn btn-danger"
                  @click="destroy(it.id)"
                  :disabled="loading"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="!loading && list.length === 0">
            <td colspan="5" class="empty">Tidak ada data</td>
          </tr>
        </tbody>
      </table>

      <div class="loading-wrap" v-if="loading"><b-spinner small /></div>

      <!-- Pagination -->
      <div v-if="totalPage > 1" class="pager-wrap">
        <div class="pager-info">Page {{ page }} of {{ totalPage }}</div>
        <div class="pager-controls">
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="First">
            <Icon icon="mdi:chevron-double-left" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Prev">
            <Icon icon="mdi:chevron-left" width="18" height="18" />
          </button>

          <button
            v-for="p in pages"
            :key="p"
            class="num-btn"
            :class="{ active: p === page }"
            :disabled="p === page || loading"
            @click="go(p)"
          >
            {{ p }}
          </button>

          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Next">
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Last">
            <Icon icon="mdi:chevron-double-right" width="18" height="18" />
          </button>
        </div>
      </div>
      <!-- End Pagination -->
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'BusinessOpportunityAdmin',
  components: { Icon },
  data(){
    return {
      list: [],
      loading: false,
      page: 1,
      totalPage: 1,
      perPage: 10,
      status: 'pending',
      search: '',
      debouncedFetch: null
    }
  },
  created(){
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed:{
    pages(){
      const t=this.totalPage, cur=this.page, arr=[], win=5
      let s=Math.max(1, cur-2), e=Math.min(t, s+win-1)
      if (e-s<win-1) s=Math.max(1, e-win+1)
      for(let i=s;i<=e;i++) arr.push(i)
      return arr
    }
  },
  methods:{
    truncate(s,n){ return s && s.length>n ? s.slice(0,n)+'...' : (s||'') },
    debounce(fn,d){ let t=null; return (...a)=>{clearTimeout(t); t=setTimeout(()=>fn.apply(this,a), d)}},
    async fetchList(){
      this.loading=true
      try{
        const params = new URLSearchParams()
        params.set('page',this.page)
        params.set('per_page',this.perPage)
        if (this.status) params.set('status', this.status)
        if (this.search) params.set('search', this.search)
        const { data } = await axios.get(`/admin/bo?${params.toString()}`)
        this.list = (data && data.data) ? data.data : []
        this.page = (data && data.current_page) ? data.current_page : 1
        this.totalPage = (data && data.last_page) ? data.last_page : 1
      } finally {
        this.loading=false
      }
    },
    go(p){ if(p<1||p>this.totalPage||p===this.page) return; this.page=p; this.fetchList(); },
    applyStatus(){ this.page=1; this.fetchList(); },
    async approve(id){
      await axios.post(`/admin/bo/${id}/approve`)
      this.fetchList()
    },
    async reject(id){
      await axios.post(`/admin/bo/${id}/reject`)
      this.fetchList()
    },
    async destroy(id){
      if (!confirm('Yakin hapus?')) return
      await axios.delete(`/admin/bo/${id}`)
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.header-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.actions{display:flex;gap:10px;align-items:center}
.search{position:relative;min-width:260px;width:320px}
.search-icon{position:absolute;top:10px;left:12px;color:#9ca3af}
.search-input{width:100%;height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 12px 0 36px;background:#fff}
.form-control{height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 10px;background:#fff}
.card{background:#fff;border:1px solid #eef2f7;border-radius:14px;padding:16px}
.table{width:100%;border-collapse:separate;border-spacing:0}
.table thead th{background:#fafafa;border-bottom:1px solid #eef2f7;text-align:left;padding:10px;font-weight:600;color:#6b7280}
.table tbody td{border-bottom:1px solid #f3f4f6;padding:10px;vertical-align:top}
.thumb{width:100px;height:70px;object-fit:cover;border-radius:8px;border:1px solid #eee}
.thumb.placeholder{display:flex;align-items:center;justify-content:center;background:#fafafa;color:#9ca3af}
.badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:12px;text-transform:capitalize}
.badge.pending{background:#fff7ed;color:#9a3412;border:1px solid #fed7aa}
.badge.approved{background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0}
.badge.rejected{background:#fef2f2;color:#991b1b;border:1px solid #fecaca}
.row-actions{display:flex;gap:8px}
.btn{height:32px;padding:0 10px;border-radius:8px;border:1px solid #e5e7eb;background:#fff}
.btn-success{background:#10b981;border-color:#10b981;color:#fff}
.btn-warning{background:#f59e0b;border-color:#f59e0b;color:#fff}
.btn-danger{background:#ef4444;border-color:#ef4444;color:#fff}
.icon-btn{height:36px;width:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn{min-width:36px;height:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn.active{background:#2563eb;border-color:#2563eb;color:#fff}
.pager-wrap{display:flex;align-items:center;justify-content:space-between;margin-top:12px}
.pager-info{font-size:13px;color:#6b7280}
.empty{text-align:center;color:#9ca3af}
</style>